// Define user interface
export interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  token?: string;
}

// Storage keys
const USER_STORAGE_KEY = "currentUser";
const TOKEN_STORAGE_KEY = "authToken";

/**
 * Get the current authenticated user
 * @returns The authenticated user or null if not logged in
 */
export function getCurrentUser(): User | null {
  try {
    // Try to get user from sessionStorage first, then localStorage
    const userStr =
      sessionStorage.getItem(USER_STORAGE_KEY) ||
      localStorage.getItem(USER_STORAGE_KEY);

    if (!userStr) {
      return null;
    }

    const user = JSON.parse(userStr) as User;

    // If user doesn't have a token, try to get it from token storage
    if (!user.token) {
      const token = getAuthToken();
      if (token) {
        user.token = token;
      }
    }

    return user;
  } catch (error) {
    console.error("Error getting current user:", error);
    return null;
  }
}

/**
 * Get the authentication token
 * @returns The authentication token or null if not available
 */
export function getAuthToken(): string | null {
  try {
    // Try sessionStorage first, then localStorage
    return (
      sessionStorage.getItem(TOKEN_STORAGE_KEY) ||
      localStorage.getItem(TOKEN_STORAGE_KEY) ||
      null
    );
  } catch (error) {
    console.error("Error getting auth token:", error);
    return null;
  }
}

/**
 * Check if the user is authenticated
 * @returns True if the user is authenticated, false otherwise
 */
export function isAuthenticated(): boolean {
  return getAuthToken() !== null || getCurrentUser() !== null;
}

/**
 * Store authentication data in session storage
 * @param user User data
 * @param token Authentication token
 * @param rememberMe Whether to store in localStorage (persistent) or sessionStorage
 */
export function storeAuthData(
  user: User,
  token: string,
  rememberMe: boolean = false
): void {
  try {
    const storage = rememberMe ? localStorage : sessionStorage;

    // Store user data
    storage.setItem(
      USER_STORAGE_KEY,
      JSON.stringify({
        ...user,
        token,
      })
    );

    // Store token separately
    storage.setItem(TOKEN_STORAGE_KEY, token);
  } catch (error) {
    console.error("Error storing auth data:", error);
  }
}

/**
 * Clear authentication data from storage
 */
export function clearAuthData(): void {
  try {
    // Clear from both session and local storage
    sessionStorage.removeItem(USER_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch (error) {
    console.error("Error clearing auth data:", error);
  }
}

/**
 * Fetch wrapper that includes Authorization header automatically
 * Use this instead of raw fetch() for authenticated API calls
 * @param url The URL to fetch
 * @param options Fetch options (headers, method, body, etc.)
 * @returns Promise<Response>
 */
export async function authFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = getAuthToken();
  const headers = new Headers(options.headers);

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  return fetch(url, {
    ...options,
    credentials: "include",
    headers,
  });
}

export default {
  getCurrentUser,
  getAuthToken,
  isAuthenticated,
  storeAuthData,
  clearAuthData,
  authFetch,
};
